import React, { useState } from "react";
import {
  ChevronLeft,
  ChevronDown,
  Signal,
  Wifi,
  BatteryFull,
  Image,
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { PresignedImage } from "./PresignedImage";
import TruncatedText from "./TruncatedText";

const now = new Date();

interface PhonePreviewProps {
  title?: string;
  children?: React.ReactNode;
  containerClassName?: string;
  // Legacy props for backward compatibility
  selectedImage?: string;
  name?: string;
  description?: string;
  price?: string;
  serviceHeader?: boolean;
}

export const PhonePreview = ({
  title,
  children,
  selectedImage,
  name,
  description,
  serviceHeader = true,
  price,
  containerClassName,
}: PhonePreviewProps) => {
  const [expanded, setExpanded] = useState(false);

  const isBlobUrl = (url: string) => {
    return (
      url.startsWith("blob:") ||
      url.startsWith("data:") ||
      url.startsWith("http")
    );
  };

  const renderImage = () => {
    if (!selectedImage) return <Image className="size-[72px] stroke-1" />;

    if (isBlobUrl(selectedImage)) {
      return (
        <img
          src={selectedImage}
          alt="Selected profile"
          className={cn("w-full h-full object-cover")}
        />
      );
    }

    return (
      <PresignedImage
        fileId={selectedImage}
        alt="Selected profile"
        className={cn("w-full h-full object-cover")}
      />
    );
  };

  // Legacy content rendering for backward compatibility
  const renderLegacyContent = () => (
    <>
      {/* Image Placeholder */}
      <div className="h-[150px] bg-gray-200 flex items-center justify-center">
        <div className="text-gray-500 text-sm">{renderImage()}</div>
      </div>

      {/* Content Section */}
      <div className="p-4 space-y-4">
        {/* Title & Description */}
        <div>
          <h3 className="text-lg font-semibold">
            {name ? (
              <TruncatedText limit={30}>{name}</TruncatedText>
            ) : (
              "Service Example"
            )}
          </h3>
          <p className="text-sm font-medium underline">Service Info</p>
          <p className="text-sm text-gray-700 mt-1 line-clamp-3">
            {description ||
              `Lorem ipsum dolor sit amet consectetur. Rhoncus fames tortor metus
            eget sollicitudin aliquam. Lorem ipsum dolor sit amet consectetur.
            Rhoncus fames tortor metus eget sollicitudin aliquam.`}
          </p>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-gray-500 mt-1 flex items-center gap-1"
          >
            Show all information
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        {/* Price */}
        <div className="flex justify-between items-center border-t pt-3">
          <span className="font-medium text-lg">Price:</span>
          <span className="font-semibold text-lg">{price || "0"}$</span>
        </div>

        {/* Additional Info */}
        <div className="border-t pt-3 space-y-2">
          <p className="text-sm font-medium underline">Additional Info</p>
        </div>
      </div>

      {/* Bottom Action Button */}
      <div className="p-4 border-t mt-10">
        <Button className="w-full">Place Order</Button>
      </div>
    </>
  );

  return (
    <div
      className={`w-[350px] bg-white rounded-[40px] shadow-md border-black border-8 overflow-hidden ${containerClassName || ""}`}
    >
      {/* iOS Status Bar */}
      <div className="relative h-[40px] bg-white border-b border-gray-200 flex items-center justify-between px-4">
        {/* Time */}
        <span className="text-sm font-semibold">{`${now.getHours()}:${now.getMinutes()}`}</span>

        {/* Dynamic Island */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="w-24 h-6 rounded-full bg-black/90" />
        </div>

        {/* Status icons (fake placeholders) */}
        <div className="flex items-center gap-1">
          <div className="flex items-center gap-1 text-gray-800">
            <Signal className="w-4 h-4" />
            <Wifi className="w-4 h-4" />
            <BatteryFull className="w-5 h-5" />
          </div>
        </div>
      </div>
      {/* Mobile notch + header */}
      {serviceHeader && (
        <div className="relative h-[40px] bg-white border-b">
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
            <ChevronLeft className="w-5 h-5" />
          </div>
          <div className="text-center text-sm font-medium leading-[40px]">
            <TruncatedText limit={30}>{title || name || "Service Example"}</TruncatedText>
          </div>
        </div>
      )}

      {/* Content - either children or legacy content */}
      {children || renderLegacyContent()}
    </div>
  );
};
